/**
 * Clasificador de `ChunkLoadError` — el error que deja una pestaña vieja con los
 * submenús muertos después de un despliegue: el `index.html` cargado pide los
 * chunks perezosos con los hashes del build anterior, esos ficheros ya no
 * existen y el CDN responde el `index.html` del SPA.
 *
 * Se clasifica por TIPO (`name`), nunca por el texto del mensaje. Buscar una
 * palabra dentro del mensaje ya costó dos incidentes en este repo (el 401 del
 * segundo factor tratado como sesión vencida por la palabra "expirado", y tres
 * fallos distintos bajo la etiqueta "ms_cards no pudo"): el mensaje es prosa y
 * cambia, el nombre del tipo es contrato.
 */
const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';

/** Tope de eslabones a desenvolver: corta cadenas largas o circulares. */
const MAX_ESLABONES = 5;

/**
 * `true` solo si el error —o alguno de los errores que lo envuelven— es un
 * `ChunkLoadError`.
 *
 * Se desenvuelve la cadena porque el error casi nunca llega pelado al
 * `ErrorHandler`: una promesa rechazada dentro de la zona llega como
 * `{ rejection }`, y un error re-lanzado llega como `{ cause }`. Desenvolver
 * sigue siendo mirar el TIPO de cada eslabón, no su mensaje.
 */
export function isChunkLoadError(error: unknown): boolean {
  const vistos = new Set<unknown>();
  const pendientes: unknown[] = [error];

  while (pendientes.length > 0 && vistos.size < MAX_ESLABONES) {
    const actual = pendientes.shift();
    if (actual === null || actual === undefined || vistos.has(actual)) continue;
    vistos.add(actual);

    if (esChunkLoadError(actual)) return true;

    // Se encolan AMBAS ramas: un eslabón puede traer `rejection` y `cause`, y
    // quedarse con la primera dejaría la otra sin mirar.
    const propiedades = comoObjeto(actual);
    if (propiedades) pendientes.push(propiedades['rejection'], propiedades['cause']);
  }

  return false;
}

function esChunkLoadError(valor: unknown): boolean {
  const propiedades = comoObjeto(valor);
  return propiedades?.['name'] === CHUNK_LOAD_ERROR_NAME;
}

function comoObjeto(valor: unknown): Record<string, unknown> | null {
  if (typeof valor !== 'object' || valor === null) return null;
  return valor as Record<string, unknown>;
}
